import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import api from '../Axios/axios';
import './AddCrane.scss';

const FIXED_DEPOTS = [
  { name: 'Gebze AVP', address: 'Gebze AVP' },
  { name: 'Demirciler', address: 'Demirciler' },
  { name: 'Pendik G\u00f6\u00e7beyli', address: 'Pendik G\u00f6\u00e7beyli' },
];

const initialFormData = {
  serialNo: '',
  ownerId: '',
  brandName: '',
  code: '',
  height: '',
  bom: '',
  maxTonnage: '',
  creationDate: new Date().toISOString(),
  type: 'Chassis',
  hasConnectionEquipment: false,
  connectionEquipmentCount: 0,
  warehouseId: '',
  currentSiteName: '',
  currentSiteAddress: '',
  isManualRented: false,
  manualRentalCompany: '',
  manualRentalTaxNo: '',
  manualRentalAddress: '',
  manualRentalAuthorizedPerson: '',
  manualRentalPhone: '',
  manualRentalEmail: '',
  manualRentalFee: '',
  manualRentalPeriod: '',
  manualRentalInvoiceInstallmentCount: '',
  manualRentalCommissionRate: '10',
  manualRentalStartDate: '',
  manualRentalEndDate: '',
  manualRentalLineItems: [],
};

const initialOwnerForm = {
  name: '',
  taxNo: '',
  address: '',
  authorizedPerson1: '',
  phoneNumber1: '',
  email: '',
};

const normalizeName = (value) =>
  String(value || '')
    .trim()
    .toLocaleLowerCase('tr-TR');

const parseCurrencyValue = (value) => {
  const normalized = String(value || '')
    .replace(/\s/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  const parsedValue = Number(normalized);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const parsePositiveInteger = (value) => {
  const parsedValue = Number(String(value || '').trim().replace(',', '.'));
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const formatCurrencyValue = (value) => {
  const parsedValue = parseCurrencyValue(value);
  if (!parsedValue) return '';

  return parsedValue.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getResponseData = (response) => response?.data?.data || response?.data?.Data || response?.data || null;

const getCreatedId = (response) => {
  const data = getResponseData(response);
  return Number(data?.id || data?.Id || data || 0);
};

const appendValue = (formData, key, value) => {
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    formData.append(key, value);
  }
};

const appendNumber = (formData, key, value) => {
  const parsedValue = Number(value);
  if (Number.isFinite(parsedValue)) {
    formData.append(key, parsedValue);
  }
};

const calculateMonthCount = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return null;
  const monthDiff = (end.getFullYear() - start.getFullYear()) * 12 + end.getMonth() - start.getMonth();
  return Math.max(monthDiff + 1, 1);
};

const AddCrane = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [craneOwners, setCraneOwners] = useState([]);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [isOwnerPickerOpen, setIsOwnerPickerOpen] = useState(false);
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [ownerForm, setOwnerForm] = useState(initialOwnerForm);
  const [isOwnerSubmitting, setIsOwnerSubmitting] = useState(false);
  const [depots, setDepots] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCraneOwners();
    fetchDepots();
  }, []);

  const fixedDepotOptions = useMemo(
    () =>
      FIXED_DEPOTS.map((fixedDepot) => {
        const existingDepot = depots.find(
          (depot) => normalizeName(depot.name) === normalizeName(fixedDepot.name)
        );

        return {
          ...fixedDepot,
          id: existingDepot?.id || '',
        };
      }),
    [depots]
  );

  const selectedDepot = fixedDepotOptions.find(
    (depot) => String(depot.id) === String(formData.warehouseId)
  );
  const isInWarehouse = Boolean(formData.warehouseId);
  const isLocationLockedByWarehouse = isInWarehouse && !formData.isManualRented;
  const selectedOwner = craneOwners.find((owner) => String(owner.id) === String(formData.ownerId));
  const filteredOwners = useMemo(() => {
    const search = normalizeName(ownerSearch);
    const sortedOwners = [...craneOwners].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), 'tr-TR')
    );

    if (!search) return sortedOwners;

    return sortedOwners
      .filter((owner) =>
        [owner.name, owner.taxNo]
          .some((field) => normalizeName(field).includes(search))
      );
  }, [craneOwners, ownerSearch]);

  const fetchCraneOwners = async () => {
    try {
      const response = await api.get('/Company');
      const owners = (response?.data?.data || [])
        .filter((company) => company.hasTowerCrane)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr-TR'));
      setCraneOwners(owners);
    } catch (error) {
      console.error('Crane owners could not be loaded:', error);
      setErrorMessage('Kule vin\u00e7 sahipleri y\u00fcklenemedi.');
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await api.get('/Warehouse');
      const existingDepots = response?.data?.data || [];
      const missingFixedDepots = FIXED_DEPOTS.filter(
        (fixedDepot) =>
          !existingDepots.some((depot) => normalizeName(depot.name) === normalizeName(fixedDepot.name))
      );

      if (missingFixedDepots.length === 0) {
        setDepots(existingDepots);
        return;
      }

      const createResults = await Promise.allSettled(
        missingFixedDepots.map((fixedDepot) => api.post('/Warehouse', fixedDepot))
      );
      const createdAnyDepot = createResults.some((result) => result.status === 'fulfilled');

      if (createdAnyDepot) {
        const refreshedResponse = await api.get('/Warehouse');
        setDepots(refreshedResponse?.data?.data || []);
      } else {
        console.warn('Fixed depots could not be created, continuing with existing depots.');
        setDepots(existingDepots);
      }
    } catch (error) {
      console.warn('Depots could not be loaded, continuing without warehouse list:', error);
      setDepots([]);
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleManualRentalFeeChange = (event) => {
    const value = event.target.value.replace(/[^\d.,]/g, '');
    setFormData((current) => ({
      ...current,
      manualRentalFee: value,
    }));
  };

  const handleManualRentalFeeBlur = () => {
    setFormData((current) => ({
      ...current,
      manualRentalFee: formatCurrencyValue(current.manualRentalFee),
    }));
  };

  const handleManualRentalLineItemChange = (index, field, value) => {
    setFormData((current) => ({
      ...current,
      manualRentalLineItems: current.manualRentalLineItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleManualRentalLineItemAmountChange = (index, value) => {
    handleManualRentalLineItemChange(index, 'unitPrice', value.replace(/[^\d.,]/g, ''));
  };

  const handleManualRentalLineItemAmountBlur = (index) => {
    setFormData((current) => ({
      ...current,
      manualRentalLineItems: current.manualRentalLineItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, unitPrice: formatCurrencyValue(item.unitPrice) } : item
      ),
    }));
  };

  const addManualRentalLineItem = () => {
    setFormData((current) => ({
      ...current,
      manualRentalLineItems: [
        ...current.manualRentalLineItems,
        { description: '', quantity: '1', unitPrice: '' },
      ],
    }));
  };

  const removeManualRentalLineItem = (index) => {
    setFormData((current) => ({
      ...current,
      manualRentalLineItems: current.manualRentalLineItems.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleDepotChange = (event) => {
    const warehouseId = event.target.value;

    setFormData((current) => ({
      ...current,
      warehouseId,
      currentSiteName: warehouseId ? '' : current.currentSiteName,
      currentSiteAddress: warehouseId ? '' : current.currentSiteAddress,
    }));
  };

  const handleOwnerSelect = (owner) => {
    setFormData((current) => ({
      ...current,
      ownerId: owner.id,
    }));
    setOwnerSearch(owner.name || '');
    setIsOwnerPickerOpen(false);
  };

  const handleOwnerFormChange = (event) => {
    const { name, value } = event.target;
    setOwnerForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleOwnerCreate = async (event) => {
    event.preventDefault();
    if (isOwnerSubmitting) return;

    const ownerName = ownerForm.name.trim();
    if (!ownerName) {
      setErrorMessage('Firma ad\u0131 zorunludur.');
      return;
    }

    try {
      setIsOwnerSubmitting(true);
      setErrorMessage('');
      const response = await api.post('/Company/crane-owner', {
        name: ownerName,
        taxNo: ownerForm.taxNo.trim() || null,
        address: ownerForm.address.trim() || null,
        authorizedPerson1: ownerForm.authorizedPerson1.trim() || null,
        phoneNumber1: ownerForm.phoneNumber1.trim() || null,
        email: ownerForm.email.trim() || null,
      });
      const createdOwner = getResponseData(response);
      const normalizedOwner = {
        ...createdOwner,
        id: createdOwner?.id || createdOwner?.Id,
        name: createdOwner?.name || createdOwner?.Name || ownerName,
        taxNo: createdOwner?.taxNo || createdOwner?.TaxNo || ownerForm.taxNo,
        phoneNumber1: createdOwner?.phoneNumber1 || createdOwner?.PhoneNumber1 || ownerForm.phoneNumber1,
        hasTowerCrane: true,
      };

      if (!normalizedOwner.id) {
        await fetchCraneOwners();
        setOwnerSearch(ownerName);
      } else {
        setCraneOwners((current) =>
          [...current.filter((owner) => String(owner.id) !== String(normalizedOwner.id)), normalizedOwner]
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr-TR'))
        );
        handleOwnerSelect(normalizedOwner);
      }

      setOwnerForm(initialOwnerForm);
      setIsOwnerModalOpen(false);
      setSuccessMessage('Vin\u00e7 sahibi firma eklendi ve se\u00e7ildi.');
    } catch (error) {
      const apiMessage =
        error?.response?.data?.messages?.join(' ') ||
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Firma eklenemedi. L\u00fctfen bilgileri kontrol edin.';
      setErrorMessage(apiMessage);
    } finally {
      setIsOwnerSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setErrorMessage('');
    setSuccessMessage('');

    if (!formData.ownerId) {
      setErrorMessage('Kule vin\u00e7 sahibi se\u00e7ilmelidir.');
      return;
    }

    if (!formData.serialNo.trim()) {
      setErrorMessage('Seri no zorunludur.');
      return;
    }

    if (!formData.brandName.trim()) {
      setErrorMessage('Marka zorunludur.');
      return;
    }

    if (!formData.code.trim()) {
      setErrorMessage('Vin\u00e7 kodu / model zorunludur.');
      return;
    }

    const height = parsePositiveInteger(formData.height);
    const maxTonnage = parsePositiveInteger(formData.maxTonnage);
    const jibLength = parsePositiveInteger(formData.bom);
    if (!height || !maxTonnage) {
      setErrorMessage('Y\u00fckseklik ve max tonaj alanlar\u0131 ge\u00e7erli say\u0131 olmal\u0131d\u0131r.');
      return;
    }

    if (!jibLength) {
      setErrorMessage('Bom bilgisi ge\u00e7erli say\u0131 olmal\u0131d\u0131r.');
      return;
    }

    const effectiveIsInWarehouse = isInWarehouse && !formData.isManualRented;
    const rentalPeriod = Number(formData.manualRentalPeriod || calculateMonthCount(formData.manualRentalStartDate, formData.manualRentalEndDate) || 0);
    const invoiceInstallmentCount = Number(formData.manualRentalInvoiceInstallmentCount || rentalPeriod);
    const rentalFee = parseCurrencyValue(formData.manualRentalFee);
    const rentalCommissionRate = Number(formData.manualRentalCommissionRate || 10);
    const rentalLineItems = (formData.manualRentalLineItems || [])
      .map((item) => ({
        description: String(item.description || '').trim(),
        quantity: Number(item.quantity || 1),
        unitPrice: parseCurrencyValue(item.unitPrice),
      }))
      .filter((item) => item.description && item.quantity > 0 && item.unitPrice > 0);
    const rentalLineItemsTotal = rentalLineItems.reduce(
      (total, item) => total + item.quantity * item.unitPrice,
      0
    );
    const rentalContractPrice = rentalFee * invoiceInstallmentCount + rentalLineItemsTotal;

    if (!effectiveIsInWarehouse && (!formData.currentSiteName.trim() || !formData.currentSiteAddress.trim())) {
      setErrorMessage('Depo se\u00e7ilmediyse veya vin\u00e7 kiradaysa \u015fantiye ad\u0131 ve \u015fantiye adresi zorunludur.');
      return;
    }

    if (formData.isManualRented) {
      if (!formData.manualRentalCompany.trim() || !rentalFee || !formData.manualRentalStartDate || !formData.manualRentalEndDate) {
        setErrorMessage('Kiradaki vin\u00e7 i\u00e7in kirac\u0131 firma, kira bedeli, ba\u015flang\u0131\u00e7 ve biti\u015f tarihi zorunludur.');
        return;
      }

      if (!rentalPeriod || !invoiceInstallmentCount) {
        setErrorMessage('Kiradaki vin\u00e7 i\u00e7in kira s\u00fcresi veya fatura taksit say\u0131s\u0131 ge\u00e7erli olmal\u0131d\u0131r.');
        return;
      }

      if (!Number.isFinite(rentalCommissionRate) || rentalCommissionRate < 0) {
        setErrorMessage('Komisyon oran\u0131 ge\u00e7erli olmal\u0131d\u0131r.');
        return;
      }
    }

    try {
      setIsSubmitting(true);

      const payload = {
        ...formData,
        brandId: null,
        brandName: formData.brandName.trim(),
        isInWarehouse: effectiveIsInWarehouse,
        warehouseId: effectiveIsInWarehouse ? Number(formData.warehouseId) : null,
        currentSiteName: effectiveIsInWarehouse ? null : formData.currentSiteName.trim(),
        currentSiteAddress: effectiveIsInWarehouse ? null : formData.currentSiteAddress.trim(),
        isManualRented: false,
        manualRentalCompany: null,
        manualRentalFee: null,
        manualRentalStartDate: null,
        manualRentalEndDate: null,
        isManualReserved: false,
        manualReserveCompany: null,
        manualReserveStartDate: null,
        manualReserveEndDate: null,
        hasWalkingGear: formData.type === 'Walking',
        height,
        bom: String(formData.bom || '').trim(),
        maxTonnage,
        connectionEquipmentCount: formData.hasConnectionEquipment
          ? Number(formData.connectionEquipmentCount || 0)
          : 0,
      };

      const craneResponse = await api.post('/TowerCrane', payload);
      const towerCraneId = getCreatedId(craneResponse);

      if (formData.isManualRented) {
        if (!towerCraneId) {
          throw new Error('Kule vin\u00e7 olu\u015fturuldu ancak kiralama i\u00e7in vin\u00e7 id bilgisi al\u0131namad\u0131.');
        }

        const rentalFormData = new FormData();
        appendValue(rentalFormData, 'TowerCraneId', towerCraneId);
        appendValue(rentalFormData, 'ManualTenantCompanyName', formData.manualRentalCompany.trim());
        appendValue(rentalFormData, 'ManualTenantTaxNo', formData.manualRentalTaxNo.trim());
        appendValue(rentalFormData, 'ManualTenantAddress', formData.manualRentalAddress.trim() || formData.currentSiteAddress.trim());
        appendValue(rentalFormData, 'ManualTenantAuthorizedPerson', formData.manualRentalAuthorizedPerson.trim());
        appendValue(rentalFormData, 'ManualTenantPhone', formData.manualRentalPhone.trim());
        appendValue(rentalFormData, 'ManualTenantEmail', formData.manualRentalEmail.trim());
        appendValue(rentalFormData, 'ManualWorkSiteName', formData.currentSiteName.trim());
        appendValue(rentalFormData, 'Type', formData.type);
        appendValue(rentalFormData, 'HasWalkingGear', formData.type === 'Walking' ? 'true' : 'false');
        appendNumber(rentalFormData, 'InstalledHeight', height);
        appendNumber(rentalFormData, 'JibLength', jibLength);
        appendNumber(rentalFormData, 'RentalPeriod', rentalPeriod);
        appendNumber(rentalFormData, 'Rent', rentalFee);
        appendNumber(rentalFormData, 'ContractPrice', rentalContractPrice);
        appendNumber(rentalFormData, 'InvoiceInstallmentCount', invoiceInstallmentCount);
        appendNumber(rentalFormData, 'CommissionRate', rentalCommissionRate);
        appendValue(rentalFormData, 'FirstInvoiceDate', formData.manualRentalStartDate);
        appendValue(rentalFormData, 'RentalStartDate', formData.manualRentalStartDate);
        appendValue(rentalFormData, 'RentalEndDate', formData.manualRentalEndDate);

        rentalLineItems.forEach((item, index) => {
          rentalFormData.append(`LineItems[${index}].Description`, item.description);
          rentalFormData.append(`LineItems[${index}].Quantity`, item.quantity);
          rentalFormData.append(`LineItems[${index}].UnitPrice`, item.unitPrice);
          rentalFormData.append(`LineItems[${index}].Amount`, item.quantity * item.unitPrice);
          rentalFormData.append(`LineItems[${index}].ItemType`, 'OneTime');
          rentalFormData.append(`LineItems[${index}].SortOrder`, index);
        });

        await api.post('/Rental', rentalFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setSuccessMessage(formData.isManualRented ? 'Kule vin\u00e7 ve kiralama kayd\u0131 ba\u015far\u0131yla olu\u015fturuldu.' : 'Kule vin\u00e7 ba\u015far\u0131yla eklendi.');
      sessionStorage.removeItem('groupTrackingCacheV1');
      setTimeout(() => navigate('/grouptracking'), 500);
    } catch (error) {
      console.error('Crane could not be created:', error.response ? error.response.data : error);
      const apiMessage =
        error?.response?.data?.messages?.join(' ') ||
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        'Kule vin\u00e7 eklenemedi. L\u00fctfen bilgileri kontrol edin.';
      setErrorMessage(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-crane-page">
      <Navbar />
      <div className="add-crane-container">
        <div className="add-crane-header">
          <div>
            <span>{'Filo Y\u00f6netimi'}</span>
            <h2>{'Kule Vin\u00e7 Ekle'}</h2>
            <p>{'Vin\u00e7 bilgilerini, sahip firmas\u0131n\u0131 ve mevcut konumunu tek ekrandan kaydedin.'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <section className="add-crane-section">
            <div className="add-crane-section-title">
              <strong>{'Temel Bilgiler'}</strong>
              <small>{'Seri, sahip ve teknik bilgiler'}</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{'Seri No'}</label>
                <input
                  type="text"
                  name="serialNo"
                  value={formData.serialNo}
                  onChange={handleInputChange}
                  placeholder="EMKC-001"
                  required
                />
              </div>

              <div className="form-group crane-owner-field">
                <label>{'Kule Vin\u00e7 Sahibi'}</label>
                <div className="owner-picker-row">
                  <div className="owner-search-input-wrap">
                    <input
                      type="text"
                      value={ownerSearch}
                      onChange={(event) => {
                        setOwnerSearch(event.target.value);
                        setFormData((current) => ({ ...current, ownerId: '' }));
                        setIsOwnerPickerOpen(true);
                      }}
                      onFocus={() => setIsOwnerPickerOpen(true)}
                      placeholder={'Firma ad\u0131 veya vergi no ile ara'}
                      required={!formData.ownerId}
                    />
                    {isOwnerPickerOpen && (
                      <button
                        type="button"
                        className="owner-picker-close"
                        onClick={() => setIsOwnerPickerOpen(false)}
                        aria-label="Firma aramasini kapat"
                      >
                        x
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="owner-add-button"
                    onClick={() => setIsOwnerModalOpen(true)}
                    title="\u015eirket Ekle"
                    aria-label="\u015eirket Ekle"
                  >
                    {'+ Ekle'}
                  </button>
                </div>

                {selectedOwner && (
                  <div className="selected-owner-card">
                    <strong>{selectedOwner.name}</strong>
                    <span>{selectedOwner.taxNo || 'Vergi no yok'}</span>
                  </div>
                )}

                {isOwnerPickerOpen && (
                  <div className="owner-search-results">
                    {filteredOwners.length > 0 ? (
                      filteredOwners.map((owner) => (
                        <button key={owner.id} type="button" onClick={() => handleOwnerSelect(owner)}>
                          <strong>{owner.name}</strong>
                          <span>{owner.taxNo || owner.phoneNumber1 || '-'}</span>
                        </button>
                      ))
                    ) : (
                      <div className="owner-empty-result">
                        <span>{'Firma bulunamad\u0131.'}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setOwnerForm((current) => ({ ...current, name: ownerSearch }));
                            setIsOwnerModalOpen(true);
                            setIsOwnerPickerOpen(false);
                          }}
                        >
                          {'Bu firmay\u0131 ekle'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{'Marka'}</label>
                <input
                  type="text"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleInputChange}
                  placeholder="6518 - 8 T"
                  required
                />
              </div>
              <div className="form-group">
                <label>{'Vin\u00e7 Kodu / Model'}</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  placeholder="STR-001"
                  required
                />
              </div>
            </div>

            <div className="form-row metric-row">
              <div className="form-group">
                <label>{'Y\u00fckseklik (m)'}</label>
                <input type="number" name="height" value={formData.height} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>{'Bom (m)'}</label>
                <input type="number" name="bom" value={formData.bom} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>{'Max Tonaj (T)'}</label>
                <input type="number" name="maxTonnage" value={formData.maxTonnage} onChange={handleInputChange} required />
              </div>
            </div>
          </section>

          <section className="add-crane-section">
            <div className="add-crane-section-title">
              <strong>{'Konum ve Durum'}</strong>
              <small>{'Depo, \u015fantiye ve mevcut kiralama bilgisi'}</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{'Zemin Tipi'}</label>
                <select name="type" value={formData.type} onChange={handleInputChange} required>
                  <option value="Chassis">{'\u015easeli'}</option>
                  <option value="Anchored">{'Ankarajl\u0131'}</option>
                  <option value="Walking">{'Y\u00fcr\u00fcy\u00fc\u015fl\u00fc'}</option>
                </select>
              </div>
              <div className="form-group connection-equipment-field">
                <span>{'Ba\u011flant\u0131 Ekipman\u0131 Var m\u0131?'}</span>
                <button
                  type="button"
                  className={`connection-equipment-box ${formData.hasConnectionEquipment ? 'checked' : ''}`}
                  aria-pressed={formData.hasConnectionEquipment}
                  aria-label="Baglanti ekipmani var mi?"
                  onClick={() =>
                    setFormData((current) => ({
                      ...current,
                      hasConnectionEquipment: !current.hasConnectionEquipment,
                    }))
                  }
                >
                  {formData.hasConnectionEquipment ? '\u2713' : ''}
                </button>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{'Depo'}</label>
                <select name="warehouseId" value={formData.warehouseId} onChange={handleDepotChange}>
                  <option value="">{'Depoda de\u011fil, \u015fantiyede'}</option>
                  {fixedDepotOptions.map((depot) => (
                    <option key={depot.name} value={depot.id} disabled={!depot.id}>
                      {depot.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{'Se\u00e7ili Konum'}</label>
                <input type="text" value={isLocationLockedByWarehouse ? selectedDepot?.name : '\u015eantiye bilgisi girilecek'} disabled />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{'\u015eantiye Ad\u0131'}</label>
                <input
                  type="text"
                  name="currentSiteName"
                  value={formData.currentSiteName}
                  onChange={handleInputChange}
                  disabled={isLocationLockedByWarehouse}
                  required={!isLocationLockedByWarehouse}
                />
              </div>
              <div className="form-group">
                <label>{'\u015eantiye Adresi'}</label>
                <input
                  type="text"
                  name="currentSiteAddress"
                  value={formData.currentSiteAddress}
                  onChange={handleInputChange}
                  disabled={isLocationLockedByWarehouse}
                  required={!isLocationLockedByWarehouse}
                />
              </div>
            </div>

            <div className="crane-status-panel">
              <div className="crane-status-copy">
                <strong>{'Mevcut Durum'}</strong>
                <span>{'Vin\u00e7 halihaz\u0131rda kiradaysa mevcut kiralama bilgisini girin.'}</span>
              </div>
              <div className="status-toggle-grid">
                <div className="manual-rental-toggle">
                  <span>{'Kirada'}</span>
                  <small>{'Mevcut kiralama bilgisini takipte g\u00f6sterir.'}</small>
                  <button
                    type="button"
                    className={`connection-equipment-box ${formData.isManualRented ? 'checked' : ''}`}
                    aria-pressed={formData.isManualRented}
                    onClick={() =>
                      setFormData((current) => ({
                        ...current,
                        isManualRented: !current.isManualRented,
                        warehouseId: !current.isManualRented ? '' : current.warehouseId,
                      }))
                    }
                  >
                    {formData.isManualRented ? '\u2713' : ''}
                  </button>
                </div>

              </div>
            </div>

          </section>

          {formData.isManualRented && (
              <section className="add-crane-section conditional-panel rental-entry-panel">
                <div className="conditional-panel-heading">
                  <div>
                    <strong>{'Aktif Kiralama Bilgisi'}</strong>
                    <span>{'Bu bilgiler kiralama takip, fatura plan\u0131 ve kazan\u00e7 hesab\u0131na i\u015flenir.'}</span>
                  </div>
                  <small>{'%10 komisyon sadece kira bedellerinden hesaplan\u0131r'}</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{'Kirac\u0131 Firma'}</label>
                    <input type="text" name="manualRentalCompany" value={formData.manualRentalCompany} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>{'Vergi No / TCKN'}</label>
                    <input type="text" name="manualRentalTaxNo" value={formData.manualRentalTaxNo} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="form-row three-column-row">
                  <div className="form-group">
                    <label>{'Yetkili'}</label>
                    <input type="text" name="manualRentalAuthorizedPerson" value={formData.manualRentalAuthorizedPerson} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>{'Telefon'}</label>
                    <input type="text" name="manualRentalPhone" value={formData.manualRentalPhone} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>{'E-posta'}</label>
                    <input type="email" name="manualRentalEmail" value={formData.manualRentalEmail} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{'Firma / \u015eantiye Adresi'}</label>
                    <input type="text" name="manualRentalAddress" value={formData.manualRentalAddress} onChange={handleInputChange} placeholder="Boş bırakılırsa şantiye adresi kullanılır" />
                  </div>
                  <div className="form-group">
                    <label>{'Ayl\u0131k Kira Bedeli'}</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      name="manualRentalFee"
                      value={formData.manualRentalFee}
                      onChange={handleManualRentalFeeChange}
                      onBlur={handleManualRentalFeeBlur}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{'Kira Ba\u015flang\u0131\u00e7 Tarihi'}</label>
                    <input type="date" name="manualRentalStartDate" value={formData.manualRentalStartDate} onChange={handleInputChange} required />
                  </div>
                  <div className="form-group">
                    <label>{'Kira Biti\u015f Tarihi'}</label>
                    <input type="date" name="manualRentalEndDate" value={formData.manualRentalEndDate} onChange={handleInputChange} required />
                  </div>
                </div>

                <div className="form-row three-column-row">
                  <div className="form-group">
                    <label>{'Kira S\u00fcresi (Ay)'}</label>
                    <input
                      type="number"
                      min="1"
                      name="manualRentalPeriod"
                      value={formData.manualRentalPeriod}
                      onChange={handleInputChange}
                      placeholder={String(calculateMonthCount(formData.manualRentalStartDate, formData.manualRentalEndDate) || '')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{'Fatura Taksit Say\u0131s\u0131'}</label>
                    <input
                      type="number"
                      min="1"
                      name="manualRentalInvoiceInstallmentCount"
                      value={formData.manualRentalInvoiceInstallmentCount}
                      onChange={handleInputChange}
                      placeholder="Kira süresi kadar"
                    />
                  </div>
                  <div className="form-group">
                    <label>{'Komisyon Oran\u0131 (%)'}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      name="manualRentalCommissionRate"
                      value={formData.manualRentalCommissionRate}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="manual-rental-services">
                  <div className="manual-rental-services-head">
                    <div>
                      <strong>{'Ek Hizmetler'}</strong>
                      <span>{'Montaj, kurulum, nakliye gibi kalemler ilk faturaya eklenir ve do\u011frudan kazan\u00e7 say\u0131l\u0131r.'}</span>
                    </div>
                    <button type="button" onClick={addManualRentalLineItem}>
                      {'+ Ek Hizmet'}
                    </button>
                  </div>

                  {formData.manualRentalLineItems.length === 0 ? (
                    <div className="manual-rental-service-empty">
                      {'Ek hizmet yoksa bo\u015f b\u0131rakabilirsiniz.'}
                    </div>
                  ) : (
                    <div className="manual-rental-service-list">
                      {formData.manualRentalLineItems.map((item, index) => (
                        <div className="manual-rental-service-row" key={`manual-rental-line-${index}`}>
                          <div className="form-group">
                            <label>{'Hizmet'}</label>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(event) => handleManualRentalLineItemChange(index, 'description', event.target.value)}
                              placeholder="Montaj, nakliye, kurulum..."
                            />
                          </div>
                          <div className="form-group">
                            <label>{'Adet'}</label>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(event) => handleManualRentalLineItemChange(index, 'quantity', event.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label>{'Birim Tutar'}</label>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={item.unitPrice}
                              onChange={(event) => handleManualRentalLineItemAmountChange(index, event.target.value)}
                              onBlur={() => handleManualRentalLineItemAmountBlur(index)}
                              placeholder="0,00"
                            />
                          </div>
                          <button type="button" onClick={() => removeManualRentalLineItem(index)}>
                            {'Kald\u0131r'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
          )}

          {errorMessage && <p className="error-message inline-feedback">{errorMessage}</p>}
          {successMessage && <p className="success-message inline-feedback">{successMessage}</p>}

          <div className="form-row submit-row">
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </form>
      </div>

      {isOwnerModalOpen && (
        <div className="owner-modal-backdrop" role="dialog" aria-modal="true">
          <form className="owner-modal" onSubmit={handleOwnerCreate}>
            <div className="owner-modal-header">
              <div>
                <span>{'H\u0131zl\u0131 Firma Kayd\u0131'}</span>
                <h3>{'Yeni Vin\u00e7 Sahibi Ekle'}</h3>
              </div>
              <button type="button" onClick={() => setIsOwnerModalOpen(false)} aria-label="Kapat">x</button>
            </div>
            <div className="owner-modal-grid">
              <label>{'Firma Ad\u0131'}<input name="name" value={ownerForm.name} onChange={handleOwnerFormChange} required /></label>
              <label>{'Vergi No'}<input name="taxNo" value={ownerForm.taxNo} onChange={handleOwnerFormChange} /></label>
              <label>{'Yetkili'}<input name="authorizedPerson1" value={ownerForm.authorizedPerson1} onChange={handleOwnerFormChange} /></label>
              <label>{'Telefon'}<input name="phoneNumber1" value={ownerForm.phoneNumber1} onChange={handleOwnerFormChange} /></label>
              <label>{'E-posta'}<input type="email" name="email" value={ownerForm.email} onChange={handleOwnerFormChange} /></label>
              <label className="wide">{'Adres'}<input name="address" value={ownerForm.address} onChange={handleOwnerFormChange} /></label>
            </div>
            <div className="owner-modal-actions">
              <button type="button" onClick={() => setIsOwnerModalOpen(false)}>{'Vazge\u00e7'}</button>
              <button type="submit" disabled={isOwnerSubmitting}>
                {isOwnerSubmitting ? 'Kaydediliyor...' : 'Kaydet ve Se\u00e7'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddCrane;
